import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check, ThumbsUp, ThumbsDown } from "lucide-react";
import { articles, getArticle } from "../data/articles";
import { products, hubs } from "../data/mock";
import { JoinBand, Reveal, Faq, Kick } from "../components/Shared";
import Seo, { breadcrumbSchema, faqSchema, articleSchema } from "../components/Seo";
import NotFound from "./NotFound";

/* Inline renderer: supports **bold** and [text](url) */
function Inline({ text }) {
  const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g).filter(Boolean);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (m) {
          const [, label, href] = m;
          if (href.startsWith("/")) {
            return (
              <Link key={i} to={href} style={{ borderBottom: "1px solid #fff" }}>
                {label}
              </Link>
            );
          }
          return (
            <a key={i} href={href} rel="noopener noreferrer" target="_blank" style={{ borderBottom: "1px solid #fff" }}>
              {label}
            </a>
          );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
}

function Block({ b }) {
  if (b.type === "h2") return <h2>{b.text}</h2>;
  if (b.type === "h3") return <h3>{b.text}</h3>;
  if (b.type === "quote") return <blockquote>{b.text}</blockquote>;
  if (b.type === "list")
    return (
      <ul className="prose-ul">
        {b.items.map((t, i) => (
          <li key={i}>
            <Inline text={t} />
          </li>
        ))}
      </ul>
    );
  if (b.type === "olist")
    return (
      <ol className="prose-ol">
        {b.items.map((t, i) => (
          <li key={i}>
            <Inline text={t} />
          </li>
        ))}
      </ol>
    );
  if (b.type === "takeaways")
    return (
      <div className="takeaways">
        <p className="tk-title">{b.title}</p>
        <ul>
          {b.items.map((t, i) => (
            <li key={i}>
              <Check size={15} strokeWidth={3} /> <span><Inline text={t} /></span>
            </li>
          ))}
        </ul>
      </div>
    );
  if (b.type === "steps")
    return (
      <div className="steps">
        {b.items.map((s, i) => (
          <div className="step" key={i}>
            <span className="stepnum">{i + 1}</span>
            <div>
              <h3>{s.title}</h3>
              <p>
                <Inline text={s.text} />
              </p>
            </div>
          </div>
        ))}
      </div>
    );
  if (b.type === "table")
    return (
      <div className="reftable-wrap">
        {b.title && <p className="tk-title">{b.title}</p>}
        <table className="reftable">
          <thead>
            <tr>
              {b.head.map((h, i) => (
                <th key={i}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {b.rows.map((r, i) => (
              <tr key={i}>
                {r.map((c, j) => (
                  <td key={j}>
                    <Inline text={c} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  return (
    <p>
      <Inline text={b.text} />
    </p>
  );
}

function Helpful() {
  const [voted, setVoted] = useState(false);
  const vote = (v) => {
    setVoted(true);
    toast.success(v ? "Noted. More scrolls like this one." : "Noted. We will sharpen the next one.");
  };
  return (
    <div className="helpful">
      <span>Was this scroll helpful?</span>
      {voted ? (
        <span className="hp-done">Feedback received.</span>
      ) : (
        <div className="hp-btns">
          <button onClick={() => vote(true)}>
            <ThumbsUp size={14} /> Yes, helpful
          </button>
          <button onClick={() => vote(false)}>
            <ThumbsDown size={14} /> Not really
          </button>
        </div>
      )}
    </div>
  );
}

export default function ArticlePage() {
  const { slug } = useParams();
  const article = getArticle(slug);
  if (!article) return <NotFound />;

  const product = products.find((p) => p.slug === article.relatedProduct);
  const idx = articles.findIndex((a) => a.slug === slug);
  const related = [1, 2, 3].map((n) => articles[(idx + n) % articles.length]);

  return (
    <>
      <Seo
        title={article.metaTitle}
        description={article.metaDescription}
        path={`/mindset/${article.slug}`}
        image={article.hero}
        type="article"
        schemas={[
          articleSchema(article),
          ...(article.faq ? [faqSchema(article.faq)] : []),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Mindset", path: "/mindset" },
            { name: article.title, path: `/mindset/${article.slug}` },
          ]),
        ]}
      />
      <div className="article-body" style={{ paddingTop: 64 }}>
        <div className="wrap">
          <div className="crumbs" style={{ marginBottom: 26 }}>
            <Link to="/">Home</Link>
            <span className="sep">/</span>
            <Link to="/mindset">Mindset</Link>
            <span className="sep">/</span>
            <span className="cur">{article.title}</span>
          </div>

          <article className="prose">
            <p className="akicker">{article.kicker}</p>
            <h1 className="atitle">{article.title}</h1>
            <p className="abyline">
              By <strong>Ninja Athletic Club</strong> · The Dojo · Fire, not fluff
            </p>
            <p className="ameta2">
              Updated {article.updated} · {article.readTime} · {article.category}
            </p>

            <figure className="afig">
              <img src={article.hero} alt={article.heroAlt} />
              <figcaption>{article.heroCaption}</figcaption>
            </figure>

            <div className="sanswer">
              <p className="sa-label">The short answer</p>
              <p className="sa-text">{article.shortAnswer}</p>
            </div>

            {article.blocks.map((b, i) => (
              <Block b={b} key={i} />
            ))}

            {article.faq && (
              <>
                <h2>Frequently asked questions</h2>
                <div style={{ margin: "6px 0 10px" }}>
                  <Faq items={article.faq} />
                </div>
              </>
            )}

            {article.bottomLine && (
              <>
                <h2>The bottom line</h2>
                <p>{article.bottomLine}</p>
              </>
            )}

            {article.sources && (
              <div className="sources">
                <p className="tk-title">Sources</p>
                <ol>
                  {article.sources.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ol>
              </div>
            )}

            <Helpful />
          </article>
        </div>
      </div>

      <div className="wrap">
        <div className="article-cta">
          {product && (
            <Reveal>
              <Link className="cta-product" to={`/shop/${product.slug}`} style={{ display: "flex", height: "100%" }}>
                <img src={product.artThumb} alt={product.altArt} loading="lazy" />
                <div className="cp-body">
                  <span className="cp-tag">Hang the reminder</span>
                  <h3>{product.title}</h3>
                  <p className="cp-price">Canvas print, from ${product.sizes[0].price}</p>
                  <span className="cutlink">Shop Print</span>
                </div>
              </Link>
            </Reveal>
          )}
          <Reveal className="cta-hubs" delay="d1">
            <span className="cp-tag">Keep walking the way</span>
            {article.siblingHubs.map((h) => (
              <Link key={h} to={hubs[h].path}>
                {hubs[h].name}
                <ArrowRight size={18} />
              </Link>
            ))}
          </Reveal>
        </div>
      </div>

      <section style={{ paddingTop: 0 }}>
        <div className="wrap">
          <Reveal>
            <Kick k="巻">Recommended reading</Kick>
            <h2 className="sec">More from the scrolls.</h2>
          </Reveal>
          <div className="grid3" style={{ marginTop: 36 }}>
            {related.map((a, i) => (
              <Reveal key={a.slug} delay={`d${i + 1}`}>
                <Link className="acard islink" to={`/mindset/${a.slug}`} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                  <div className="amedia">
                    <span className="abadge">{a.category}</span>
                    <img src={a.hero} alt={a.heroAlt} loading="lazy" />
                  </div>
                  <div className="abody">
                    <h3>{a.title}</h3>
                    <p>{a.metaDescription}</p>
                    <span className="ameta">{a.readTime}</span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <JoinBand />
    </>
  );
}
